import { useEffect, useRef } from 'react';
import { calculateGSMSNetCostFromMfg } from '../../bidAnalysisFormulas';
import {
  formatDateForChartAPI,
  getMissingFieldsReport,
  preserveGlobalFields,
} from '../../bidAnalysisTemplate.logic';
import { whlsFeeCalculationNC } from '../../ncBlendedWhlsFeeCalculator';
import type { MissingFieldsReport } from '../../bidAnalysisTemplate.logic';

export interface UseNCEffectsDeps {
  bidDetails: any;
  activePackSizeTab: number | null;
  activeYearTab: number | null;
  yearFormDataRef: React.MutableRefObject<Record<number, Record<number, any>>>;
  yearFormData: Record<number, Record<number, any>>;
  formData: any;
  gsmsHndlgType: string;
  templateData: any;
  BidAnalysisStore: any;
  bidId: string | undefined;
  allPackSizeTabs: any[];
  packSizeFormData: Record<number, any>;
  supplierName: string;
  loadContractPriceRanking: (
    startDate?: string,
    endDate?: string,
    forceRefresh?: boolean,
    limit?: number
  ) => void;
  setFormData: (updater: ((prev: any) => any) | any) => void;
  setYearFormData: (updater: ((prev: Record<number, Record<number, any>>) => Record<number, Record<number, any>>) | Record<number, Record<number, any>>) => void;
  setMissingFieldsReport: (report: MissingFieldsReport | null) => void;
}

export function useNCEffects(deps: UseNCEffectsDeps): void {
  const {
    bidDetails,
    activePackSizeTab,
    activeYearTab,
    yearFormDataRef,
    yearFormData,
    formData,
    gsmsHndlgType,
    templateData,
    BidAnalysisStore,
    bidId,
    allPackSizeTabs,
    packSizeFormData,
    supplierName,
    loadContractPriceRanking,
    setFormData,
    setYearFormData,
    setMissingFieldsReport,
  } = deps;

  // NC CPR tracking refs (private to this hook)
  const prevNcYearTabRef = useRef<number | null>(null);
  const prevNcPackSizeTabRef = useRef<number | null>(null);
  const prevNcStartDateRef = useRef<string | null | undefined>(null);
  const prevNcEndDateRef = useRef<string | null | undefined>(null);

  // ── NC year sync with discount calculation ─────────────────────────────
  useEffect(() => {
    const currentTemplateType = bidDetails?.template_type || 'mas';
    if (currentTemplateType !== 'nc') {
      return;
    }

    const syncYearDataWithDiscount = async () => {
      const currentYearDataFromRef =
        activePackSizeTab !== null
          ? yearFormDataRef.current[activePackSizeTab]?.[activeYearTab as number] || null
          : null;
      const currentYearDataFromState =
        activePackSizeTab !== null
          ? yearFormData[activePackSizeTab]?.[activeYearTab as number] || null
          : null;

      const currentYearData = currentYearDataFromRef || currentYearDataFromState;

      if (!currentYearData || Object.keys(currentYearData).length === 0) {
        return;
      }
      const { suggested_bid_price: _suggested_bid_price, ...cleanedYearData } = currentYearData;

      const sharedNdcFields = {
        selectedMfgNdc: cleanedYearData.selectedMfgNdc || formData.selectedMfgNdc || null,
        mfg_ndc: cleanedYearData.mfg_ndc || formData.mfg_ndc || null,
        gsms_mfg_ndc: cleanedYearData.gsms_mfg_ndc || formData.gsms_mfg_ndc || null,
        gsms_ndc: cleanedYearData.gsms_ndc || formData.gsms_ndc || null,
      };

      let supplierDiscountForYear: number | undefined;
      let supplierCodeForYear: string | undefined;
      let baseYearData: any = {};
      let baseYearDataFromRef: any = {};

      if (activePackSizeTab !== null) {
        baseYearData = yearFormData[activePackSizeTab]?.[0] || {};
        baseYearDataFromRef = yearFormDataRef.current[activePackSizeTab]?.[0] || {};
        supplierDiscountForYear =
          baseYearDataFromRef.supplierDiscount ??
          baseYearData.supplierDiscount ??
          cleanedYearData.supplierDiscount ??
          formData.supplierDiscount;
        supplierCodeForYear =
          baseYearDataFromRef.supplierCode ??
          baseYearData.supplierCode ??
          cleanedYearData.supplierCode ??
          formData.supplierCode;

        if (supplierDiscountForYear === undefined && supplierCodeForYear) {
          supplierDiscountForYear = BidAnalysisStore.getSupplierDiscount(supplierCodeForYear);
        }
      } else {
        supplierDiscountForYear = cleanedYearData.supplierDiscount ?? formData.supplierDiscount;
        supplierCodeForYear = cleanedYearData.supplierCode ?? formData.supplierCode;

        if (supplierDiscountForYear === undefined && supplierCodeForYear) {
          supplierDiscountForYear = BidAnalysisStore.getSupplierDiscount(supplierCodeForYear);
        }
      }

      let yearSpecificTargetLaunchDate: string | Date | null | undefined;
      if (activeYearTab === 0 || activeYearTab === null) {
        yearSpecificTargetLaunchDate = formData.targetLaunchDate;
      } else {
        yearSpecificTargetLaunchDate =
          cleanedYearData.targetLaunchDate || formData.targetLaunchDate;
      }

      const existingBlendedWhlsFee = cleanedYearData.blendedWhlsFee;
      const hasExistingBlendedWhlsFee =
        existingBlendedWhlsFee !== null &&
        existingBlendedWhlsFee !== undefined &&
        existingBlendedWhlsFee !== '' &&
        existingBlendedWhlsFee !== '0.00' &&
        !isNaN(parseFloat(String(existingBlendedWhlsFee)));

      const syncedData: any = {
        ...cleanedYearData,
        ...sharedNdcFields,
        ...(supplierDiscountForYear !== undefined && { supplierDiscount: supplierDiscountForYear }),
        ...(supplierCodeForYear !== undefined && { supplierCode: supplierCodeForYear }),
        contracts: formData.contracts,
        contractsData: formData.contractsData,
        supplierName: formData.supplierName || cleanedYearData.supplierName,
        gsmsHndlgType: gsmsHndlgType || cleanedYearData.gsmsHndlgType,
        analysisDate: formData.analysisDate,
        targetLaunchDate: yearSpecificTargetLaunchDate,
        analysisType: formData.analysisType,
        ...(cleanedYearData._gsmsProfitManuallySet !== undefined && {
          _gsmsProfitManuallySet: cleanedYearData._gsmsProfitManuallySet,
        }),
        ...(cleanedYearData._gsmsCostFromApi !== undefined && {
          _gsmsCostFromApi: cleanedYearData._gsmsCostFromApi,
        }),
        ...(hasExistingBlendedWhlsFee && { blendedWhlsFee: String(existingBlendedWhlsFee) }),
      };

      if (
        syncedData.gsmsProfit !== null &&
        syncedData.gsmsProfit !== undefined &&
        syncedData.gsmsProfit !== '' &&
        syncedData.gsmsProfit !== 0
      ) {
        syncedData._gsmsProfitManuallySet = true;
      }

      const yearUsage =
        parseFloat(syncedData.ncEstAnnualUsage || syncedData.targetAnnualUsage || '0') || 0;
      const yearBidPrice = parseFloat(syncedData.suggestedBidPrice || '0') || 0;
      if (yearUsage > 0 && yearBidPrice > 0) {
        syncedData.contractAnnualRevenue = (yearUsage * yearBidPrice).toFixed(2);
        const profit = parseFloat(syncedData.gsmsProfit || '0') || 0;
        if (profit !== 0) {
          syncedData.contractAnnualGP = (yearUsage * yearBidPrice * (profit / 100)).toFixed(2);
        }
      }

      const isBaseYear = activeYearTab === 0 || activeYearTab === null;
      const shouldRecalculateForBaseYear =
        isBaseYear && yearSpecificTargetLaunchDate && syncedData.suggestedBidPrice;

      let effectiveDateForWhlsFee: string | Date | null | undefined = yearSpecificTargetLaunchDate;
      if (!isBaseYear && activeYearTab !== null && activeYearTab > 0) {
        const baseDate = (templateData as any)?.targetLaunchDate || formData.targetLaunchDate;
        if (baseDate) {
          try {
            const base = typeof baseDate === 'string' ? new Date(baseDate) : baseDate;
            if (base instanceof Date && !isNaN(base.getTime())) {
              const yearDate = new Date(base);
              yearDate.setFullYear(base.getFullYear() + activeYearTab);
              effectiveDateForWhlsFee = yearDate;
            }
          } catch (_e) { /* fallback */ }
        }
      }

      if (
        (!hasExistingBlendedWhlsFee || shouldRecalculateForBaseYear) &&
        effectiveDateForWhlsFee &&
        syncedData.suggestedBidPrice
      ) {
        const suggestedBidPrice = parseFloat(syncedData.suggestedBidPrice) || 0;
        const vaAnnualNcEst = parseFloat(syncedData.vaAnnualNcEst) || 0;
        const dodAnnualNcEst = parseFloat(syncedData.dodAnnualNcEst) || 0;
        const ihsAnnualNcEst = parseFloat(syncedData.ihsAnnualNcEst) || 0;
        const bopAnnualNcEst = parseFloat(syncedData.bopAnnualNcEst) || 0;

        if (suggestedBidPrice > 0) {
          try {
            const recalculatedBlendedWhlsFee = whlsFeeCalculationNC(
              effectiveDateForWhlsFee,
              suggestedBidPrice,
              vaAnnualNcEst,
              dodAnnualNcEst,
              ihsAnnualNcEst,
              bopAnnualNcEst
            );
            syncedData.blendedWhlsFee = recalculatedBlendedWhlsFee.toFixed(2);
          } catch (_error) { /* leave empty */ }
        }
      }

      const gsmsCostFromMfg = parseFloat(syncedData.gsmsCostFromMfg) || 0;

      if (gsmsCostFromMfg > 0) {
        const supplierBulkNdc = syncedData.selectedMfgNdc || syncedData.mfg_ndc || '';
        const finalSupplierCode =
          supplierCodeForYear ||
          (supplierBulkNdc && supplierBulkNdc.length >= 5 ? supplierBulkNdc.substring(0, 5) : '');

        let discount = supplierDiscountForYear;
        if (!discount && finalSupplierCode) {
          discount = BidAnalysisStore.getSupplierDiscount(finalSupplierCode);
        }

        if (discount !== undefined && discount !== null && discount > 0) {
          const expectedNetCost = calculateGSMSNetCostFromMfg(
            gsmsCostFromMfg,
            supplierBulkNdc,
            discount
          );
          syncedData.gsmsNetCostFromMfg = expectedNetCost.toFixed(2);
          syncedData.supplierDiscount = discount;
          syncedData.supplierCode = finalSupplierCode;
        }
      }

      if (activePackSizeTab !== null && activeYearTab !== null && activeYearTab !== 5) {
        yearFormDataRef.current = {
          ...yearFormDataRef.current,
          [activePackSizeTab]: {
            ...yearFormDataRef.current[activePackSizeTab],
            [activeYearTab]: {
              ...syncedData,
              gsmsNetCostFromMfg: syncedData.gsmsNetCostFromMfg,
              supplierDiscount: syncedData.supplierDiscount,
              supplierCode: syncedData.supplierCode,
              ...(syncedData.blendedWhlsFee && { blendedWhlsFee: syncedData.blendedWhlsFee }),
              ...(syncedData.targetLaunchDate && { targetLaunchDate: syncedData.targetLaunchDate }),
            },
          },
        };
      }

      const preservedGlobalData = preserveGlobalFields(syncedData, formData);
      const updatedFormDataForUI = {
        ...preservedGlobalData,
        gsmsNetCostFromMfg: syncedData.gsmsNetCostFromMfg,
        gsmsCostFromMfg: syncedData.gsmsCostFromMfg,
        supplierDiscount: syncedData.supplierDiscount,
        supplierCode: syncedData.supplierCode,
        ...(syncedData.blendedWhlsFee && { blendedWhlsFee: syncedData.blendedWhlsFee }),
      };

      setFormData(updatedFormDataForUI);

      if (activePackSizeTab !== null && activeYearTab !== null && activeYearTab !== 5) {
        setYearFormData(prev => ({
          ...prev,
          [activePackSizeTab]: {
            ...prev[activePackSizeTab],
            [activeYearTab]: {
              ...syncedData,
              gsmsNetCostFromMfg: syncedData.gsmsNetCostFromMfg,
              supplierDiscount: syncedData.supplierDiscount,
              supplierCode: syncedData.supplierCode,
              ...(syncedData.blendedWhlsFee && { blendedWhlsFee: syncedData.blendedWhlsFee }),
              ...(syncedData.targetLaunchDate && { targetLaunchDate: syncedData.targetLaunchDate }),
            },
          },
        }));
      }
    };

    syncYearDataWithDiscount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePackSizeTab, activeYearTab, bidDetails?.template_type]);

  // ── NC contract price ranking by year/usage dates ──────────────────────
  useEffect(() => {
    const currentTemplateType = bidDetails?.template_type || 'mas';
    if (currentTemplateType !== 'nc') {
      return;
    }

    const currentYearData =
      activePackSizeTab !== null && activeYearTab !== null && activeYearTab !== 5
        ? yearFormData[activePackSizeTab]?.[activeYearTab] || formData
        : formData;
    const hasUsageDates = !!(
      currentYearData.usagePeriodStartDate && currentYearData.usagePeriodEndDate
    );
    const hasRequiredFields = !!(bidId && currentYearData.conceptName && currentYearData.packSize);

    const startDate = currentYearData.usagePeriodStartDate;
    const endDate = currentYearData.usagePeriodEndDate;
    const yearTabChanged = prevNcYearTabRef.current !== activeYearTab;
    const packSizeTabChanged = prevNcPackSizeTabRef.current !== activePackSizeTab;
    const startDateChanged = prevNcStartDateRef.current !== startDate;
    const endDateChanged = prevNcEndDateRef.current !== endDate;

    const shouldCallAPI =
      hasUsageDates &&
      hasRequiredFields &&
      (yearTabChanged || packSizeTabChanged || startDateChanged || endDateChanged);

    if (shouldCallAPI) {
      const startDateStr = formatDateForChartAPI(startDate);
      const endDateStr = formatDateForChartAPI(endDate);
      loadContractPriceRanking(startDateStr || undefined, endDateStr || undefined, false, 500);

      prevNcYearTabRef.current = activeYearTab;
      prevNcPackSizeTabRef.current = activePackSizeTab;
      prevNcStartDateRef.current = startDate;
      prevNcEndDateRef.current = endDate;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    bidDetails?.template_type,
    activePackSizeTab,
    activeYearTab,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    activePackSizeTab !== null && activeYearTab !== null && activeYearTab !== 5
      ? yearFormData[activePackSizeTab]?.[activeYearTab]?.usagePeriodStartDate
      : formData.usagePeriodStartDate,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    activePackSizeTab !== null && activeYearTab !== null && activeYearTab !== 5
      ? yearFormData[activePackSizeTab]?.[activeYearTab]?.usagePeriodEndDate
      : formData.usagePeriodEndDate,
    bidId,
  ]);

  // ── NC missing fields report ───────────────────────────────────────────
  useEffect(() => {
    if (!bidDetails) {
      setMissingFieldsReport(null);
      return;
    }

    const currentTemplateType = bidDetails?.template_type || 'mas';

    if (currentTemplateType === 'nc') {
      const report = getMissingFieldsReport({
        yearFormData,
        templateType: currentTemplateType,
        bidDetails,
        packSizeTabs: allPackSizeTabs,
        packSizeFormData,
        formData,
        supplierName,
        marketSalesTrendData: [],
        contractPriceRanking: [],
      });
      setMissingFieldsReport(report);
    } else {
      setMissingFieldsReport(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bidDetails, allPackSizeTabs, packSizeFormData, yearFormData, formData, supplierName]);
}
